import React from "react";
import { PageHeader } from "../components/PageHeader/PageHeader";

// ─── Types ────────────────────────────────────────────────────────────────────

type SLAPriority = "Critical" | "High" | "Medium" | "Low";
type TimeUnit = "hours" | "days";
type ContractType = "company" | "asset";
type ContractStatus = "active" | "expiring_soon" | "expired" | "draft";
type SLACompliancePeriod = "30D" | "90D" | "6M" | "1Y";
type SLAComplianceStatus = "MET" | "BREACHED" | "OPEN";

interface SLAWorkOrderRecord {
  id: string;
  woNumber: string;
  title: string;
  priority: SLAPriority;
  onsiteStatus: SLAComplianceStatus;
  onsiteActualH: number | null;
  onsiteTargetH: number;
  responseStatus: SLAComplianceStatus;
  responseActualH: number | null;
  responseTargetH: number;
  resolutionStatus: SLAComplianceStatus;
  resolutionActualH: number | null;
  resolutionTargetH: number;
}

interface CompanyTicket {
  id: string;
  number: string;
  title: string;
  priority: SLAPriority;
  responseStatus: SLAComplianceStatus;
  responseActualH: number | null;
  responseTargetH: number;
  resolutionStatus: SLAComplianceStatus;
  resolutionActualH: number | null;
  resolutionTargetH: number;
}

interface SLAComplianceData {
  totalWOs: number;
  overallPct: number;
  onsitePct: number;
  responsePct: number;
  resolutionPct: number;
  trendOverall: number;
  trendOnsite: number;
  trendResponse: number;
  trendResolution: number;
  recentWOs: SLAWorkOrderRecord[];
}

interface SLALevel {
  id: string;
  priority: SLAPriority;
  responseTime: number;
  responseUnit: TimeUnit;
  resolutionTime: number;
  resolutionUnit: TimeUnit;
}

interface Contract {
  id: string;
  contractNumber: string;
  type: ContractType;
  status: ContractStatus;
  assetId?: string;
  assetName?: string;
  effectiveDate: string;
  expiryDate: string;
  paymentTerms: string;
  laborRate: number;
  laborDiscount?: number;
  laborMarginCap?: number;
  partsDiscountRate: number;
  partsMarginCap?: number;
  serviceDiscount?: number;
  serviceMarginCap?: number;
  travelRate: number;
  travelDiscount?: number;
  travelMarginCap?: number;
  slaLevels: SLALevel[];
}

export interface CompanyDetailPageProps {
  companyId: string;
  companyName: string;
  onBack?: () => void;
  onOpenCompany?: (companyId: string, companyName: string) => void;
}

interface SubCompany {
  id: string;
  code: string;
  name: string;
  address: string;
  tags?: string[];
}

type Tab = "Overview" | "SLA Compliance" | "Activity Log" | "History" | "Documentation" | "Assets" | "Contacts" | "Sub Companies";

// ─── Mock contracts per company ───────────────────────────────────────────────

const DEFAULT_SLA_LEVELS: SLALevel[] = [
  { id: "sla-1", priority: "Critical", responseTime: 4,  responseUnit: "hours", resolutionTime: 8,  resolutionUnit: "hours" },
  { id: "sla-2", priority: "High",     responseTime: 8,  responseUnit: "hours", resolutionTime: 24, resolutionUnit: "hours" },
  { id: "sla-3", priority: "Medium",   responseTime: 24, responseUnit: "hours", resolutionTime: 3,  resolutionUnit: "days"  },
  { id: "sla-4", priority: "Low",      responseTime: 48, responseUnit: "hours", resolutionTime: 5,  resolutionUnit: "days"  },
];

const COMPANY_CONTRACTS: Record<string, Contract[]> = {
  "acme-international": [
    {
      id: "c1",
      contractNumber: "CTR-2024-0041",
      type: "company",
      status: "active",
      effectiveDate: "2024-01-01",
      expiryDate: "2026-12-31",
      paymentTerms: "Net 30",
      laborRate: 85,
      partsDiscountRate: 15,
      travelRate: 1.2,
      slaLevels: DEFAULT_SLA_LEVELS,
    },
    {
      id: "c2",
      contractNumber: "CTR-2025-0012",
      type: "asset",
      status: "expiring_soon",
      assetId: "M-2152",
      assetName: "CNC Machine A",
      effectiveDate: "2025-03-01",
      expiryDate: "2026-05-28",
      paymentTerms: "Net 60",
      laborRate: 95,
      partsDiscountRate: 20,
      travelRate: 1.5,
      slaLevels: [
        { id: "s1", priority: "Critical", responseTime: 2, responseUnit: "hours", resolutionTime: 4,  resolutionUnit: "hours" },
        { id: "s2", priority: "High",     responseTime: 4, responseUnit: "hours", resolutionTime: 12, resolutionUnit: "hours" },
      ],
    },
  ],
  "acme-packaging": [
    {
      id: "c7",
      contractNumber: "CTR-2025-0072",
      type: "asset",
      status: "active",
      assetId: "PM-2000",
      assetName: "Packmaster 2000",
      effectiveDate: "2025-04-01",
      expiryDate: "2026-03-31",
      paymentTerms: "Net 30",
      laborRate: 90,
      partsDiscountRate: 18,
      travelRate: 1.4,
      slaLevels: DEFAULT_SLA_LEVELS,
    },
  ],
};

const COMPANY_SUB_COMPANIES: Record<string, SubCompany[]> = {
  "acme-international": [
    { id: "acme-packaging", code: "C-000002", name: "Acme Packaging", address: "220 Harbor Road, Rotterdam, Netherlands" },
    { id: "acme-logistics", code: "C-000003", name: "Acme Logistics", address: "45 Baker Street, Manchester, UK" },
  ],
  "meridian-electrical": [
    { id: "meridian-uk", code: "C-000007", name: "Meridian UK Ltd", address: "12 Baker Street, London, UK" },
  ],
  "coca-cola": [
    { id: "coca-cola-georgia", code: "C-000011", name: "CocaCola Georgia", address: "One Coca-Cola Plaza, Atlanta, GA 30313, USA" },
    { id: "coca-cola-california", code: "C-000014", name: "CocaCola California", address: "1000 Sansome St, San Francisco, CA 94111, USA" },
    { id: "coca-cola-texas", code: "C-000017", name: "CocaCola Texas", address: "6425 United Plaza Blvd, Houston, TX 77057, USA" },
    { id: "coca-cola-new-york", code: "C-000020", name: "CocaCola New York", address: "711 5th Ave, New York, NY 10022, USA" },
  ],
  "coca-cola-georgia": [
    { id: "coca-cola-atlanta", code: "C-000012", name: "CocaCola Atlanta", address: "191 Peachtree St NE, Atlanta, GA 30303, USA" },
    { id: "coca-cola-savannah", code: "C-000013", name: "CocaCola Savannah", address: "2 E Bay St, Savannah, GA 31401, USA" },
  ],
  "coca-cola-california": [
    { id: "coca-cola-los-angeles", code: "C-000015", name: "CocaCola Los Angeles", address: "1200 S Vermont Ave, Los Angeles, CA 90006, USA" },
    { id: "coca-cola-san-francisco", code: "C-000016", name: "CocaCola San Francisco", address: "1000 Sansome St, San Francisco, CA 94111, USA" },
  ],
  "coca-cola-texas": [
    { id: "coca-cola-houston", code: "C-000018", name: "CocaCola Houston", address: "6425 United Plaza Blvd, Houston, TX 77057, USA" },
    { id: "coca-cola-austin", code: "C-000019", name: "CocaCola Austin", address: "301 Congress Ave, Austin, TX 78701, USA" },
  ],
  "coca-cola-new-york": [
    { id: "coca-cola-new-york-city", code: "C-000021", name: "CocaCola New York City", address: "711 5th Ave, New York, NY 10022, USA" },
    { id: "coca-cola-buffalo", code: "C-000022", name: "CocaCola Buffalo", address: "617 Main St, Buffalo, NY 14203, USA" },
  ],
};

const RECENT_WOS: SLAWorkOrderRecord[] = [
  { id: "w1", woNumber: "WO-2026-0021", title: "CNC Spindle Bearing Failure",      priority: "Critical", onsiteStatus: "MET",      onsiteActualH: 1.5,  onsiteTargetH: 2,  responseStatus: "MET",      responseActualH: 2.5,  responseTargetH: 4,  resolutionStatus: "MET",      resolutionActualH: 6.5,  resolutionTargetH: 8   },
  { id: "w2", woNumber: "WO-2026-0019", title: "Packmaster Belt Replacement",      priority: "High",     onsiteStatus: "MET",      onsiteActualH: 3.1,  onsiteTargetH: 4,  responseStatus: "MET",      responseActualH: 5.2,  responseTargetH: 8,  resolutionStatus: "BREACHED", resolutionActualH: 28,   resolutionTargetH: 24  },
  { id: "w3", woNumber: "WO-2026-0017", title: "Conveyor Motor Overheating",       priority: "Medium",   onsiteStatus: "BREACHED", onsiteActualH: 9.5,  onsiteTargetH: 8,  responseStatus: "MET",      responseActualH: 18,   responseTargetH: 24, resolutionStatus: "MET",      resolutionActualH: 2.1,  resolutionTargetH: 3   },
  { id: "w4", woNumber: "WO-2026-0015", title: "Hydraulic Seal Leak",              priority: "Critical", onsiteStatus: "BREACHED", onsiteActualH: 2.9,  onsiteTargetH: 2,  responseStatus: "BREACHED", responseActualH: 5.8,  responseTargetH: 4,  resolutionStatus: "BREACHED", resolutionActualH: 10,   resolutionTargetH: 8   },
  { id: "w5", woNumber: "WO-2026-0013", title: "Electrical Panel Inspection",      priority: "Low",      onsiteStatus: "MET",      onsiteActualH: 20,   onsiteTargetH: 24, responseStatus: "MET",      responseActualH: 31,   responseTargetH: 48, resolutionStatus: "OPEN",     resolutionActualH: null, resolutionTargetH: 120 },
  { id: "w6", woNumber: "WO-2026-0011", title: "Rotary Machine Spinner Repair",    priority: "High",     onsiteStatus: "OPEN",     onsiteActualH: null, onsiteTargetH: 4,  responseStatus: "MET",      responseActualH: 3.9,  responseTargetH: 8,  resolutionStatus: "OPEN",     resolutionActualH: null, resolutionTargetH: 24  },
  { id: "w7", woNumber: "WO-2026-0009", title: "Air Compressor Filter Change",     priority: "Medium",   onsiteStatus: "MET",      onsiteActualH: 7.2,  onsiteTargetH: 8,  responseStatus: "MET",      responseActualH: 20,   responseTargetH: 24, resolutionStatus: "MET",      resolutionActualH: 2.5,  resolutionTargetH: 3   },
  { id: "w8", woNumber: "WO-2026-0007", title: "Forklift Battery Replacement",     priority: "Low",      onsiteStatus: "MET",      onsiteActualH: 19,   onsiteTargetH: 24, responseStatus: "BREACHED", responseActualH: 52,   responseTargetH: 48, resolutionStatus: "MET",      resolutionActualH: 4.2,  resolutionTargetH: 5   },
];

const RECENT_TICKETS: CompanyTicket[] = [
  { id: "t1", number: "TKT-10321", title: "CNC Spindle Bearing Failure",   priority: "Critical", responseStatus: "MET",      responseActualH: 1.8,  responseTargetH: 4,  resolutionStatus: "MET",      resolutionActualH: 5.5,  resolutionTargetH: 8   },
  { id: "t2", number: "TKT-10318", title: "Packmaster Belt Inspection",    priority: "High",     responseStatus: "MET",      responseActualH: 6.2,  responseTargetH: 8,  resolutionStatus: "BREACHED", resolutionActualH: 30,   resolutionTargetH: 24  },
  { id: "t3", number: "TKT-10315", title: "Hydraulic System Overhaul",     priority: "Critical", responseStatus: "BREACHED", responseActualH: 5.1,  responseTargetH: 4,  resolutionStatus: "BREACHED", resolutionActualH: 14,   resolutionTargetH: 8   },
  { id: "t4", number: "TKT-10312", title: "Annual Safety Inspection",      priority: "Medium",   responseStatus: "MET",      responseActualH: 20,   responseTargetH: 24, resolutionStatus: "OPEN",     resolutionActualH: null, resolutionTargetH: 120 },
  { id: "t5", number: "TKT-10309", title: "Conveyor Motor Maintenance",    priority: "Low",      responseStatus: "MET",      responseActualH: 40,   responseTargetH: 48, resolutionStatus: "MET",      resolutionActualH: 3.0,  resolutionTargetH: 5   },
  { id: "t6", number: "TKT-10306", title: "Electrical Panel Replacement",  priority: "High",     responseStatus: "BREACHED", responseActualH: 10,   responseTargetH: 8,  resolutionStatus: "OPEN",     resolutionActualH: null, resolutionTargetH: 24  },
];

const SLA_COMPLIANCE_DATA: Record<SLACompliancePeriod, SLAComplianceData> = {
  "30D": { totalWOs: 23,  overallPct: 85, onsitePct: 83, responsePct: 87, resolutionPct: 83, trendOverall: 3.2,  trendOnsite: 2.1,  trendResponse: 1.8,  trendResolution: -0.5,  recentWOs: RECENT_WOS },
  "90D": { totalWOs: 61,  overallPct: 82, onsitePct: 80, responsePct: 84, resolutionPct: 80, trendOverall: -1.5, trendOnsite: -0.8, trendResponse: 0.4,  trendResolution: -2.1,  recentWOs: RECENT_WOS },
  "6M":  { totalWOs: 142, overallPct: 79, onsitePct: 76, responsePct: 81, resolutionPct: 77, trendOverall: -2.8, trendOnsite: -1.9, trendResponse: -1.2, trendResolution: -3.6,  recentWOs: RECENT_WOS },
  "1Y":  { totalWOs: 278, overallPct: 76, onsitePct: 73, responsePct: 78, resolutionPct: 74, trendOverall: -4.1, trendOnsite: -3.2, trendResponse: -2.5, trendResolution: -5.2,  recentWOs: RECENT_WOS },
};

const CONTRACT_STATUS_CONFIG: Record<ContractStatus, { bg: string; color: string; label: string }> = {
  active:        { bg: "#ccefeb", color: "#00b09b", label: "ACTIVE" },
  expiring_soon: { bg: "#fff0e6", color: "#FF7A00", label: "EXPIRING SOON" },
  expired:       { bg: "#fde5f0", color: "#ee0064", label: "EXPIRED" },
  draft:         { bg: "#f3f4f6", color: "#748094", label: "DRAFT" },
};

const SLA_PRIORITY_CONFIG: Record<SLAPriority, { bg: string; color: string }> = {
  Critical: { bg: "#FEF2F2", color: "#DC2626" },
  High:     { bg: "#FFF7ED", color: "#EA580C" },
  Medium:   { bg: "#FEFCE8", color: "#CA8A04" },
  Low:      { bg: "#F0FDF4", color: "#16A34A" },
};

// ─── Company quotes & invoices ────────────────────────────────────────────────

type CompanyQuoteStatus   = "PENDING" | "ACCEPTED" | "REJECTED" | "DRAFT" | "SENT";
type CompanyInvoiceStatus = "PAID"    | "OVERDUE"  | "SENT"     | "DRAFT";

interface CompanyQuote {
  id: string; number: string; createdDate: string; status: CompanyQuoteStatus; amount: string;
}
interface CompanyInvoice {
  id: string; number: string; dueDate: string; amount: string; quoteNumber: string | null; status: CompanyInvoiceStatus;
}

const MOCK_COMPANY_QUOTES: CompanyQuote[] = [
  { id: "q1", number: "QT-2026-0001", createdDate: "12 Apr 2026", status: "ACCEPTED", amount: "1.000,00 €" },
  { id: "q2", number: "QT-2026-0003", createdDate: "20 Apr 2026", status: "SENT",     amount: "5.000,00 €" },
  { id: "q3", number: "QT-2026-0005", createdDate: "12 Nov 2025", status: "DRAFT",    amount: "2.750,00 €" },
  { id: "q4", number: "QT-2026-0007", createdDate: "15 Jan 2026", status: "REJECTED", amount: "3.200,00 €" },
  { id: "q5", number: "QT-2026-0009", createdDate: "03 Mar 2026", status: "PENDING",  amount: "8.400,00 €" },
];

const MOCK_COMPANY_INVOICES: CompanyInvoice[] = [
  { id: "i1", number: "IN-2026-0001", dueDate: "12 May 2026", amount: "1.000,00 €", quoteNumber: "QT-2026-0001", status: "PAID"    },
  { id: "i2", number: "IN-2026-0004", dueDate: "20 May 2026", amount: "5.000,00 €", quoteNumber: "QT-2026-0003", status: "SENT"    },
  { id: "i3", number: "IN-2026-0007", dueDate: "15 Apr 2026", amount: "2.750,00 €", quoteNumber: null,           status: "OVERDUE" },
  { id: "i4", number: "IN-2026-0010", dueDate: "10 Jun 2026", amount: "8.400,00 €", quoteNumber: "QT-2026-0009", status: "DRAFT"   },
  { id: "i5", number: "IN-2026-0012", dueDate: "25 Jun 2026", amount: "3.200,00 €", quoteNumber: null,           status: "SENT"    },
];

const COMPANY_QUOTE_STATUS_CFG: Record<CompanyQuoteStatus, { bg: string; color: string }> = {
  PENDING:  { bg: "#fff0e6", color: "#FF7A00" },
  ACCEPTED: { bg: "#e6f9f1", color: "#10B981" },
  REJECTED: { bg: "#fde5f0", color: "#ee0064" },
  DRAFT:    { bg: "#f3f4f6", color: "#748094" },
  SENT:     { bg: "#e6e8fe", color: "#0517f8" },
};

const COMPANY_INVOICE_STATUS_CFG: Record<CompanyInvoiceStatus, { bg: string; color: string }> = {
  PAID:    { bg: "#ccefeb", color: "#00b09b" },
  OVERDUE: { bg: "#fde5f0", color: "#ee0064" },
  SENT:    { bg: "#e6e8fe", color: "#0517f8" },
  DRAFT:   { bg: "#f3f4f6", color: "#748094" },
};

function CompanyQuoteStatusBadge({ status }: { status: CompanyQuoteStatus }) {
  const c = COMPANY_QUOTE_STATUS_CFG[status];
  return <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 6px", borderRadius: 4, fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px", background: c.bg, color: c.color, fontFamily: "var(--font-family-base)", whiteSpace: "nowrap" }}>{status}</span>;
}

function CompanyInvoiceStatusBadge({ status }: { status: CompanyInvoiceStatus }) {
  const c = COMPANY_INVOICE_STATUS_CFG[status];
  return <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 6px", borderRadius: 4, fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px", background: c.bg, color: c.color, fontFamily: "var(--font-family-base)", whiteSpace: "nowrap" }}>{status}</span>;
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// ─── Price Book mock data ─────────────────────────────────────────────────────

interface PriceBook { id: string; name: string; currency: string; productsCount: number; status: string; }

const MOCK_PRICE_BOOKS: PriceBook[] = [
  { id: "pb1", name: "Standard Price Book", currency: "USD", productsCount: 12, status: "Active" },
  { id: "pb2", name: "Premium Price Book",  currency: "USD", productsCount: 8,  status: "Active" },
];

// ─── Icons ────────────────────────────────────────────────────────────────────

const BuildingIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="#0517F8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="18" height="14" rx="1.5" />
    <path d="M7 7V4a2 2 0 012-2h4a2 2 0 012 2v3" />
    <path d="M8 12h2M12 12h2M8 16h2M12 16h2" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round">
    <path d="M4 6l4 4 4-4" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round">
    <path d="M4 10l4-4 4 4" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.5 2.5l2 2L4 12H2v-2L9.5 2.5z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M4 4l8 8M12 4l-8 8" />
  </svg>
);

const MoreIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
    <circle cx="3" cy="7" r="1.2" />
    <circle cx="7" cy="7" r="1.2" />
    <circle cx="11" cy="7" r="1.2" />
  </svg>
);

const ContractDocIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2.5" y="1.5" width="11" height="13" rx="1.5" />
    <path d="M5 5.5h6M5 8h6M5 10.5h3.5" />
  </svg>
);

const IdIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1.5" y="3" width="11" height="8" rx="1.5" />
    <path d="M4.5 7h5M4.5 9h3" />
  </svg>
);

const TypeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1.5" y="3.5" width="11" height="7" rx="1.5" />
    <path d="M4 6.5h6M4 8.5h4" />
  </svg>
);

const WebIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7" cy="7" r="5.5" />
    <path d="M7 1.5c-1.5 2-2 3.5-2 5.5s.5 3.5 2 5.5M7 1.5c1.5 2 2 3.5 2 5.5s-.5 3.5-2 5.5M1.5 7h11" />
  </svg>
);

const LocationIcon = ({ color = "#9CA3AF" }: { color?: string }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 1.5a4 4 0 014 4C11 8.5 7 13 7 13S3 8.5 3 5.5a4 4 0 014-4z" />
    <circle cx="7" cy="5.5" r="1.3" />
  </svg>
);

const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7" cy="5" r="2.5" />
    <path d="M2 12c0-2.761 2.239-5 5-5s5 2.239 5 5" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" />
    <path d="M1.5 5.5h11M4.5 1.5v2M9.5 1.5v2" />
  </svg>
);

const PortalIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#EA580C" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="12" height="12" rx="2" />
    <path d="M8 5v6M5 8h6" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
    <circle cx="7" cy="7" r="5.5" />
    <path d="M7 6.5v4" />
    <circle cx="7" cy="4.5" r="0.5" fill="currentColor" stroke="none" />
  </svg>
);

const TrendUpIcon = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1.5 8.5l3-3 2 2L9.5 3" /><path d="M7 3h2.5v2.5" />
  </svg>
);
const TrendDownIcon = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1.5 3l3 3 2-2 3 3.5" /><path d="M7 9.5h2.5V7" />
  </svg>
);
const ShieldCheckIcon = ({ color = "#0517F8" }: { color?: string }) => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7.5 1L2 3.5v4c0 3 2.5 5 5.5 6.5C10.5 12.5 13 10.5 13 7.5v-4L7.5 1z" />
    <path d="M5 7.5l2 2 3-3" />
  </svg>
);
const ClockFastIcon = ({ color = "#ff7a00" }: { color?: string }) => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="8" r="5.5" /><path d="M7.5 5.5V8l1.5 1.5" /><path d="M4.5 1.5h6" />
  </svg>
);
const CheckCircleIcon = ({ color = "#00b09b" }: { color?: string }) => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="7.5" r="6" /><path d="M4.5 7.5l2.5 2.5 3.5-4" />
  </svg>
);
const WrenchIcon = ({ color = "#748094" }: { color?: string }) => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 1.5a3.5 3.5 0 00-3.5 4.5L2 10.5A1.5 1.5 0 004 12.5l4.5-4.5A3.5 3.5 0 0010 1.5z" />
    <path d="M9 3.5l2 2" />
  </svg>
);

// ─── Sub-components ───────────────────────────────────────────────────────────

function Avatar({ initials, color }: { initials: string; color: string }) {
  return (
    <div style={{
      width: 20, height: 20, borderRadius: "50%", background: color, color: "#fff",
      fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, fontFamily: "var(--font-family-base)",
    }}>
      {initials}
    </div>
  );
}

function SidebarDetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", alignItems: "center", minHeight: 32 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 8, paddingTop: 8, paddingBottom: 8 }}>
        {icon}
        <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: "16px" }}>{label}</span>
      </div>
      <div style={{ padding: "8px 8px", fontSize: 12, fontWeight: 500, color: "#172B4D", fontFamily: "var(--font-family-base)", lineHeight: "16px" }}>
        {value}
      </div>
    </div>
  );
}

function SLAPriorityBadge({ priority }: { priority: SLAPriority }) {
  const cfg = SLA_PRIORITY_CONFIG[priority];
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 4,
      fontSize: 11, fontWeight: 700, letterSpacing: "0.3px",
      background: cfg.bg, color: cfg.color,
      fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
    }}>
      {priority.toUpperCase()}
    </span>
  );
}

function ContractTypeBadge({ type }: { type: ContractType }) {
  const isAsset = type === "asset";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 6px", borderRadius: 4,
      fontSize: 11, fontWeight: 700, letterSpacing: "-0.22px",
      background: isAsset ? "#e6e8fe" : "#f3f4f6",
      color: isAsset ? "#0517f8" : "#748094",
      fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
    }}>
      {isAsset ? "Asset" : "Company"}
    </span>
  );
}

function ContractStatusBadge({ status }: { status: ContractStatus }) {
  const cfg = CONTRACT_STATUS_CONFIG[status];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 6px", borderRadius: 4,
      fontSize: 10, fontWeight: 700, letterSpacing: "0.3px",
      background: cfg.bg, color: cfg.color,
      fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
    }}>
      {cfg.label}
    </span>
  );
}

// ─── Price Book Icon ──────────────────────────────────────────────────────────

const PriceBookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="1.5" width="9" height="13" rx="1" />
    <path d="M5 5h3M5 7.5h3M5 10h2" />
    <path d="M11 4.5h2a1 1 0 011 1v8a1 1 0 01-1 1H5" />
  </svg>
);

// ─── Price Book Card ──────────────────────────────────────────────────────────

function PriceBookCard({ book, onRemove }: { book: PriceBook; onRemove: () => void }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        border: "1px solid #E8EAED", borderRadius: 8, padding: "10px 12px",
        display: "flex", alignItems: "center", gap: 10,
        background: hovered ? "#F9FAFB" : "#fff", transition: "background 80ms",
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8, background: "#EEF0FF", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center", color: "#0517F8",
      }}>
        <PriceBookIcon />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#172B4D", fontFamily: "var(--font-family-base)", lineHeight: "18px", display: "block", marginBottom: 3 }}>
          {book.name}
        </span>
        <span style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
          {book.status} · {book.productsCount} products
        </span>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4, display: "flex", borderRadius: 4, lineHeight: 0 }}
        title="Remove"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M3 3l8 8M11 3l-8 8" />
        </svg>
      </button>
    </div>
  );
}

// ─── Contract Card (sidebar style) ───────────────────────────────────────────

function ContractCard({ contract, companyName, onClick }: { contract: Contract; companyName: string; onClick: () => void }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        border: "1px solid #E8EAED", borderRadius: 8, padding: "10px 12px",
        display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
        background: hovered ? "#F9FAFB" : "#fff", transition: "background 80ms",
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: contract.type === "asset" ? "#EFF6FF" : "#F0FDF4",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        color: contract.type === "asset" ? "#0517F8" : "#16A34A",
      }}>
        <ContractDocIcon />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#172B4D", fontFamily: "var(--font-family-base)", lineHeight: "18px", display: "block", marginBottom: 5 }}>
          {contract.contractNumber}
        </span>
        {contract.type === "asset" && contract.assetName ? (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 3,
            padding: "2px 6px", borderRadius: 4,
            fontSize: 11, fontWeight: 500, color: "#0517f8",
            background: "#e6e8fe", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
          }}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="1" width="8" height="8" rx="1.5" />
              <path d="M3.5 5h3M5 3.5v3" />
            </svg>
            {contract.assetName}
          </span>
        ) : (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 3,
            padding: "2px 6px", borderRadius: 4,
            fontSize: 11, fontWeight: 500, color: "#748094",
            background: "#f3f4f6", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
          }}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="8" height="6.5" rx="1" />
              <path d="M3 3V2a2 2 0 014 0v1" />
            </svg>
            {companyName}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Contract Detail Panel ────────────────────────────────────────────────────

function ContractDetailPanel({ contract, onClose }: { contract: Contract; onClose: () => void }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 200 }} />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: 520,
        background: "#fff", zIndex: 201,
        display: "flex", flexDirection: "column",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px",
          borderBottom: "1px solid #E8EAED",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.32px" }}>
            {contract.contractNumber}
          </span>
          <button onClick={onClose} style={{ width: 28, height: 28, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, color: "#748094" }}>
            <CloseIcon />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {/* Badges */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            <ContractTypeBadge type={contract.type} />
            <ContractStatusBadge status={contract.status} />
          </div>

          {/* Asset override callout */}
          {contract.type === "asset" && (
            <div style={{
              padding: "10px 12px", borderRadius: 8, marginBottom: 20,
              background: "#e6f9f1", border: "1px solid #a7f3d0",
              fontSize: 12, fontWeight: 500, color: "#065f46",
              fontFamily: "var(--font-family-base)", display: "flex", gap: 8, alignItems: "flex-start",
            }}>
              <InfoIcon />
              <span>Overrides the company contract on quotes, invoices &amp; work orders for <strong>{contract.assetName}</strong>.</span>
            </div>
          )}

          {/* Linked To */}
          <PanelSection title="Linked To">
            {contract.assetName && <PanelRow label="Asset" value={`${contract.assetName} · ${contract.assetId}`} />}
          </PanelSection>

          {/* Period */}
          <PanelSection title="Period">
            <PanelRow label="Effective Date" value={formatDate(contract.effectiveDate)} />
            <PanelRow label="Expiry Date" value={formatDate(contract.expiryDate)} />
            <PanelRow label="Payment Terms" value={contract.paymentTerms} />
          </PanelSection>

          {/* Rates */}
          <PanelSection title="Rates">
            <PanelRow label="Labour Rate" value={`$${contract.laborRate}/hr`} />
            <PanelRow label="Parts Discount" value={`${contract.partsDiscountRate}%`} />
            <PanelRow label="Travel Rate" value={`$${contract.travelRate}/km`} />
          </PanelSection>

          {/* SLA */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
              SLA Configuration
            </p>
            <div style={{ border: "1px solid #E8EAED", borderRadius: 8, overflow: "hidden" }}>
              {/* Header */}
              <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", padding: "8px 12px", background: "#F9FAFB", borderBottom: "1px solid #E8EAED" }}>
                {[
                  { label: "PRIORITY",   sub: null },
                  { label: "RESPONSE",   sub: "Tickets" },
                  { label: "RESOLUTION", sub: "WO + Tickets" },
                ].map(({ label, sub }) => (
                  <span key={label} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#748094", fontFamily: "var(--font-family-base)", letterSpacing: "0.3px" }}>{label}</span>
                    {sub && <span style={{ fontSize: 10, fontWeight: 500, color: "#a2aab8", fontFamily: "var(--font-family-base)" }}>{sub}</span>}
                  </span>
                ))}
              </div>
              {contract.slaLevels.map((level, idx) => (
                <div key={level.id} style={{
                  display: "grid", gridTemplateColumns: "120px 1fr 1fr",
                  padding: "10px 12px",
                  borderBottom: idx < contract.slaLevels.length - 1 ? "1px solid #F3F4F6" : "none",
                  alignItems: "center",
                }}>
                  <SLAPriorityBadge priority={level.priority} />
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#172B4D", fontFamily: "var(--font-family-base)" }}>
                    {level.responseTime} {level.responseUnit}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#172B4D", fontFamily: "var(--font-family-base)" }}>
                    {level.resolutionTime} {level.resolutionUnit}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function PanelSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, color: "#748094", fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px" }}>
        {title}
      </p>
      <div style={{ border: "1px solid #E8EAED", borderRadius: 8, overflow: "hidden" }}>
        {children}
      </div>
    </div>
  );
}

function PanelRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "9px 12px", borderBottom: "1px solid #E8EAED",
    }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)" }}>{value}</span>
    </div>
  );
}

// ─── SLA Compliance Dashboard ────────────────────────────────────────────────

function formatH(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(hours / 24);
  const h = Math.floor(hours % 24);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

function complianceColor(pct: number): string {
  return pct >= 90 ? "#00b09b" : pct >= 70 ? "#ff7a00" : "#ee0064";
}

function SLAComplianceBadge({ status }: { status: SLAComplianceStatus }) {
  const cfg = {
    MET:      { bg: "#ccefeb", color: "#00b09b", label: "MET"      },
    BREACHED: { bg: "#fde5f0", color: "#ee0064", label: "BREACHED" },
    OPEN:     { bg: "#f3f4f6", color: "#748094", label: "OPEN"     },
  }[status];
  return (
    <span style={{
      display: "inline-block", padding: "2px 6px", borderRadius: 4,
      fontSize: 10, fontWeight: 700, letterSpacing: "0.3px",
      background: cfg.bg, color: cfg.color, fontFamily: "var(--font-family-base)",
    }}>
      {cfg.label}
    </span>
  );
}

function TrendChip({ delta }: { delta: number }) {
  if (delta === 0) return null;
  const positive = delta > 0;
  const color = positive ? "#00b09b" : "#ee0064";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 11, fontWeight: 700, color, fontFamily: "var(--font-family-base)" }}>
      {positive ? <TrendUpIcon /> : <TrendDownIcon />}
      {positive ? "+" : ""}{delta.toFixed(1)}%
    </span>
  );
}

function KPICard({ label, value, delta, icon, accentColor }: {
  label: string; value: string; delta: number; icon: React.ReactNode; accentColor: string;
}) {
  return (
    <div style={{ border: "1px solid #E8EAED", borderRadius: 8, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 6, display: "flex",
          alignItems: "center", justifyContent: "center",
          background: accentColor + "18",
        }}>
          {icon}
        </div>
        <TrendChip delta={delta} />
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.44px", lineHeight: "28px" }}>
        {value}
      </div>
      <div style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
}

function ComplianceBar({ pct }: { pct: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: "#E8EAED", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: complianceColor(pct) }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color: complianceColor(pct), fontFamily: "var(--font-family-base)", minWidth: 30, textAlign: "right" }}>
        {pct}%
      </span>
    </div>
  );
}

function SLATimeCell({ status, actualH, targetH }: { status: SLAComplianceStatus; actualH: number | null; targetH: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <SLAComplianceBadge status={status} />
      <span style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
        {actualH !== null ? formatH(actualH) : "—"} / {formatH(targetH)}
      </span>
    </div>
  );
}

function RecentWOsTable({ wos }: { wos: SLAWorkOrderRecord[] }) {
  const COL = "116px 1fr 110px 110px 110px";
  const hStyle: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: "#748094", fontFamily: "var(--font-family-base)", letterSpacing: "0.3px" };
  return (
    <div style={{ border: "1px solid #E8EAED", borderRadius: 8, overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: COL, padding: "8px 14px", background: "#F9FAFB", borderBottom: "1px solid #E8EAED", gap: 8, alignItems: "center" }}>
        {["WO #", "TITLE", "ONSITE", "RESPONSE", "RESOLUTION"].map(h => <span key={h} style={hStyle}>{h}</span>)}
      </div>
      {wos.map((wo, i) => (
        <div key={wo.id} style={{
          display: "grid", gridTemplateColumns: COL, padding: "10px 14px", gap: 8, alignItems: "center",
          borderBottom: i < wos.length - 1 ? "1px solid #F3F4F6" : "none",
        }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#0517F8", fontFamily: "var(--font-family-base)" }}>{wo.woNumber}</span>
          <span style={{ fontSize: 12, fontWeight: 500, color: "#172B4D", fontFamily: "var(--font-family-base)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{wo.title}</span>
          <SLATimeCell status={wo.onsiteStatus}     actualH={wo.onsiteActualH}     targetH={wo.onsiteTargetH}     />
          <SLATimeCell status={wo.responseStatus}   actualH={wo.responseActualH}   targetH={wo.responseTargetH}   />
          <SLATimeCell status={wo.resolutionStatus} actualH={wo.resolutionActualH} targetH={wo.resolutionTargetH} />
        </div>
      ))}
    </div>
  );
}

function RecentTicketsTable({ tickets }: { tickets: CompanyTicket[] }) {
  const COL = "116px 1fr 110px 110px";
  const hStyle: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: "#748094", fontFamily: "var(--font-family-base)", letterSpacing: "0.3px" };
  return (
    <div style={{ border: "1px solid #E8EAED", borderRadius: 8, overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: COL, padding: "8px 14px", background: "#F9FAFB", borderBottom: "1px solid #E8EAED", gap: 8, alignItems: "center" }}>
        {["TKT #", "TITLE", "RESPONSE", "RESOLUTION"].map(h => <span key={h} style={hStyle}>{h}</span>)}
      </div>
      {tickets.map((t, i) => (
        <div key={t.id} style={{
          display: "grid", gridTemplateColumns: COL, padding: "10px 14px", gap: 8, alignItems: "center",
          borderBottom: i < tickets.length - 1 ? "1px solid #F3F4F6" : "none",
        }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#0517F8", fontFamily: "var(--font-family-base)" }}>{t.number}</span>
          <span style={{ fontSize: 12, fontWeight: 500, color: "#172B4D", fontFamily: "var(--font-family-base)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</span>
          <SLATimeCell status={t.responseStatus}   actualH={t.responseActualH}   targetH={t.responseTargetH}   />
          <SLATimeCell status={t.resolutionStatus} actualH={t.resolutionActualH} targetH={t.resolutionTargetH} />
        </div>
      ))}
    </div>
  );
}

function SLAComplianceTab({ companyId: _companyId }: { companyId: string }) {
  const [period, setPeriod] = React.useState<SLACompliancePeriod>("30D");
  const data = SLA_COMPLIANCE_DATA[period];
  const PERIODS: SLACompliancePeriod[] = ["30D", "90D", "6M", "1Y"];
  const sectionTitle: React.CSSProperties = { margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.26px" };

  return (
    <div>
      {/* Period filter */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
          Last updated: 6 May 2026
        </span>
        <div style={{ display: "flex", border: "1px solid #E8EAED", borderRadius: 8, overflow: "hidden" }}>
          {PERIODS.map((p, i) => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              padding: "7px 14px", border: "none", cursor: "pointer",
              fontSize: 12, fontWeight: 700, fontFamily: "var(--font-family-base)",
              background: period === p ? "#0517F8" : "#fff",
              color: period === p ? "#fff" : "#748094",
              borderRight: i < PERIODS.length - 1 ? "1px solid #E8EAED" : "none",
              transition: "background 100ms",
            }}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 24 }}>
        <KPICard label="Overall Compliance" value={`${data.overallPct}%`}    delta={data.trendOverall}     icon={<ShieldCheckIcon color="#0517F8" />}  accentColor="#0517F8" />
        <KPICard label="Onsite SLA"          value={`${data.onsitePct}%`}     delta={data.trendOnsite}      icon={<LocationIcon color="#9333ea" />}     accentColor="#9333ea" />
        <KPICard label="Response SLA"        value={`${data.responsePct}%`}   delta={data.trendResponse}    icon={<ClockFastIcon color="#ff7a00" />}    accentColor="#ff7a00" />
        <KPICard label="Resolution SLA"      value={`${data.resolutionPct}%`} delta={data.trendResolution}  icon={<CheckCircleIcon color="#00b09b" />}  accentColor="#00b09b" />
        <KPICard label="Total Work Orders"   value={`${data.totalWOs}`}       delta={0}                     icon={<WrenchIcon color="#748094" />}       accentColor="#748094" />
      </div>

      {/* Work orders */}
      <p style={{ ...sectionTitle, marginTop: 20 }}>Recent Work Orders</p>
      <RecentWOsTable wos={data.recentWOs} />

      {/* Recent tickets */}
      <p style={{ ...sectionTitle, marginTop: 24 }}>Recent Tickets</p>
      <RecentTicketsTable tickets={RECENT_TICKETS} />
    </div>
  );
}

// ─── Sub Companies Tab ─────────────────────────────────────────────────────────

function SubCompanyCard({ company, onOpen }: { company: SubCompany; onOpen?: (id: string, name: string) => void }) {
  const [hovered, setHovered] = React.useState(false);
  const visibleTag = company.tags?.[0];
  const extraTagCount = (company.tags?.length ?? 0) - 1;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onOpen?.(company.id, company.name)}
      style={{
        display: "flex", alignItems: "center", gap: 16, padding: 16,
        background: hovered ? "var(--color-bg-accent)" : "#fff",
        border: "1px solid #E8EAED", borderRadius: 8,
        cursor: onOpen ? "pointer" : "default",
        transition: "background 80ms ease",
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: "#EEF0FF", border: "1px solid #E8EAED",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <BuildingIcon />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0, fontSize: 16, fontWeight: 700, lineHeight: "24px", letterSpacing: "-0.32px",
          color: "#172B4D", fontFamily: "var(--font-family-base)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {company.name}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 500, lineHeight: "16px", color: "#748094", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap" }}>
            {company.code}
          </span>
          <span style={{ color: "#A2AAB8", fontSize: 12, lineHeight: "16px" }}>•</span>
          <span style={{
            fontSize: 12, fontWeight: 500, lineHeight: "16px", color: "#748094",
            fontFamily: "var(--font-family-base)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {company.address}
          </span>
        </div>
      </div>

      {visibleTag && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            padding: "4px 6px", borderRadius: 4, background: "#F3F4F6",
            fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
            color: "#748094", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
          }}>
            {visibleTag}
          </span>
          {extraTagCount > 0 && (
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              padding: "4px 6px", borderRadius: 4, background: "#F3F4F6",
              fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
              color: "#748094", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
            }}>
              +{extraTagCount}
            </span>
          )}
        </div>
      )}

      <button
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 20, height: 20, background: "none", border: "none",
          cursor: "pointer", padding: 0, flexShrink: 0, color: "#748094",
        }}
      >
        <MoreIcon />
      </button>
    </div>
  );
}

function SubCompaniesTab({ companyId, onOpenCompany }: { companyId: string; onOpenCompany?: (id: string, name: string) => void }) {
  const subCompanies = COMPANY_SUB_COMPANIES[companyId] ?? [];

  if (subCompanies.length === 0) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", paddingTop: 80, gap: 8,
      }}>
        <span style={{ fontSize: 32 }}>🏢</span>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)" }}>
          No Sub Companies
        </p>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
          This company has no sub companies yet.
        </p>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 16, paddingBottom: 24, display: "flex", flexDirection: "column", gap: 8 }}>
      {subCompanies.map((sub) => (
        <SubCompanyCard key={sub.id} company={sub} onOpen={onOpenCompany} />
      ))}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS: Tab[] = ["Overview", "SLA Compliance", "Activity Log", "History", "Documentation", "Assets", "Contacts", "Sub Companies"];

export function CompanyDetailPage({ companyId, companyName, onBack, onOpenCompany }: CompanyDetailPageProps) {
  const [activeTab, setActiveTab] = React.useState<Tab>("Overview");
  const [descExpanded, setDescExpanded] = React.useState(true);
  const [hoveredQuoteId, setHoveredQuoteId] = React.useState<string | null>(null);
  const [hoveredInvoiceId, setHoveredInvoiceId] = React.useState<string | null>(null);
  const [detailsCollapsed, setDetailsCollapsed] = React.useState(false);
  const [portalCollapsed, setPortalCollapsed] = React.useState(false);
  const [contractsCollapsed, setContractsCollapsed] = React.useState(false);
  const [priceBookCollapsed, setPriceBookCollapsed] = React.useState(false);
  const [assignedPriceBooks, setAssignedPriceBooks] = React.useState<PriceBook[]>([]);
  const [customFieldsCollapsed, setCustomFieldsCollapsed] = React.useState(false);
  const [selectedContract, setSelectedContract] = React.useState<Contract | null>(null);

  const subCompanies = COMPANY_SUB_COMPANIES[companyId] ?? [];
  const TAB_COUNT: Partial<Record<Tab, number>> = { Assets: 2, Contacts: 2, ...(subCompanies.length > 0 ? { "Sub Companies": subCompanies.length } : {}) };
  const companyContracts = COMPANY_CONTRACTS[companyId] ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#fff", fontFamily: "var(--font-family-base)" }}>

      <PageHeader
        variant="breadcrumb"
        breadcrumbs={[{ label: "Companies", onClick: onBack }, { label: companyName }]}
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-s)" }}>
            <button style={{
              height: 40, padding: "0 var(--spacing-s)",
              border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-s)",
              background: "var(--color-bg-primary)", cursor: "pointer",
              display: "flex", alignItems: "center", gap: "var(--spacing-2xs)",
              fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
              letterSpacing: "-0.24px", color: "var(--color-content-primary)",
            }}>
              <EditIcon />
              Edit
            </button>
            <button style={{
              width: 40, height: 40,
              border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-s)",
              background: "var(--color-bg-primary)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--color-content-secondary)",
            }}>
              <MoreIcon />
            </button>
          </div>
        }
      />

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* Left content area */}
        <div style={{ flex: 1, overflowY: "auto", minWidth: 0 }}>
          <div style={{ padding: "0 24px" }}>

            {/* Company header */}
            <div style={{ display: "flex", alignItems: "center", gap: 16, paddingTop: 16, paddingBottom: 4 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 10,
                background: "#EEF0FF", border: "1px solid #E8EAED",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <BuildingIcon />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.36px", lineHeight: "28px" }}>
                    {companyName}
                  </h1>
                </div>
                <div style={{ fontSize: 14, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: "20px", marginTop: 2 }}>
                  C-000001
                </div>
              </div>
            </div>

            {/* Tab bar */}
            <div style={{ borderBottom: "1px solid #E8EAED", display: "flex", gap: 0, paddingTop: 4 }}>
              {TABS.map((tab) => {
                const count = TAB_COUNT[tab];
                const active = activeTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      position: "relative", display: "flex", alignItems: "center", gap: 4,
                      padding: "10px 8px 12px", border: "none", background: "none", cursor: "pointer",
                      fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px", lineHeight: "16px",
                      color: active ? "#172B4D" : "#A2AAB8",
                      fontFamily: "var(--font-family-base)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tab}
                    {count !== undefined && (
                      <span style={{ fontSize: 12, fontWeight: 700, color: active ? "#172B4D" : "#A2AAB8" }}>
                        ({count})
                      </span>
                    )}
                    {active && (
                      <div style={{
                        position: "absolute", bottom: 0, left: 0, right: 0, height: 2,
                        background: "#0517F8", borderRadius: "2px 2px 0 0",
                      }} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            {activeTab === "Overview" && (
              <div style={{ paddingTop: 16, paddingBottom: 24, display: "flex", flexDirection: "column", gap: 12 }}>

                {/* Description accordion */}
                <div style={{ border: "1px solid #E8EAED", borderRadius: 8, background: "#fff", overflow: "hidden" }}>
                  <div
                    onClick={() => setDescExpanded((v) => !v)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", cursor: "pointer" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: "50%", background: "#EEF0FF",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#0517F8" strokeWidth="1.4" strokeLinecap="round">
                          <path d="M1.5 3h9M1.5 6h9M1.5 9h5" />
                        </svg>
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
                        Description
                      </span>
                    </div>
                    {descExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </div>
                  {descExpanded && (
                    <div style={{ padding: "0 16px 16px", fontSize: 12, fontWeight: 500, color: "#172B4D", fontFamily: "var(--font-family-base)", lineHeight: "16px" }}>
                      Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
                    </div>
                  )}
                </div>

                {/* ── Quotes table ────────────────────────────────────────── */}
                <div style={{ border: "1px solid #E8EAED", borderRadius: 8, background: "#fff", overflow: "hidden" }}>
                  {/* Section header */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid #E8EAED" }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#EEF0FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#0517F8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1.5" y="1.5" width="9" height="9" rx="1.5" />
                        <path d="M3.5 4.5h5M3.5 6.5h3" />
                      </svg>
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>Quotes</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#748094", fontFamily: "var(--font-family-base)" }}>({MOCK_COMPANY_QUOTES.length})</span>
                  </div>
                  {/* Table */}
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                      <colgroup>
                        <col style={{ width: 160 }} />
                        <col style={{ width: 150 }} />
                        <col style={{ width: 120 }} />
                        <col style={{ width: 130 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          {[
                            { label: "Quote Number", icon: <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><rect x="1" y="1" width="10" height="10" rx="1.5" /><path d="M3 4h2M3 6.5h6M3 9h4" /></svg> },
                            { label: "Created Date", icon: <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><rect x="1" y="2" width="10" height="9" rx="1.5" /><path d="M1 5h10M4 1v2M8 1v2" /></svg> },
                            { label: "Status",       icon: <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><circle cx="6" cy="6" r="5" /><path d="M4 6l1.5 1.5L8 4" /></svg> },
                            { label: "Amount",       icon: <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><path d="M6 1v10M8.5 3.5a2.5 1.5 0 10-5 0 2.5 1.5 0 005 0zM8.5 8.5a2.5 1.5 0 10-5 0 2.5 1.5 0 005 0z" /></svg> },
                          ].map(({ label, icon }) => (
                            <th key={label} style={{ background: "var(--color-bg-accent)", borderBottom: "1px solid var(--color-border-primary)", padding: "0 12px", height: 34, whiteSpace: "nowrap", textAlign: "left", fontWeight: "normal" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", userSelect: "none" }}>
                                {icon}{label}
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {MOCK_COMPANY_QUOTES.map((row) => (
                          <tr
                            key={row.id}
                            onMouseEnter={() => setHoveredQuoteId(row.id)}
                            onMouseLeave={() => setHoveredQuoteId(null)}
                            style={{ background: hoveredQuoteId === row.id ? "var(--color-bg-accent)" : "#fff", cursor: "pointer", transition: "background 80ms ease" }}
                          >
                            <td style={{ padding: "0 12px", height: 48, fontSize: 12, fontWeight: 700, color: "#0517F8", fontFamily: "var(--font-family-base)", borderBottom: "1px solid var(--color-border-primary)", verticalAlign: "middle", whiteSpace: "nowrap" }}>{row.number}</td>
                            <td style={{ padding: "0 12px", height: 48, fontSize: 12, fontWeight: 500, color: "#748094",  fontFamily: "var(--font-family-base)", borderBottom: "1px solid var(--color-border-primary)", verticalAlign: "middle", whiteSpace: "nowrap" }}>{row.createdDate}</td>
                            <td style={{ padding: "0 12px", height: 48, borderBottom: "1px solid var(--color-border-primary)", verticalAlign: "middle" }}><CompanyQuoteStatusBadge status={row.status} /></td>
                            <td style={{ padding: "0 12px", height: 48, fontSize: 12, fontWeight: 600, color: "#172B4D",  fontFamily: "var(--font-family-base)", borderBottom: "1px solid var(--color-border-primary)", verticalAlign: "middle", whiteSpace: "nowrap" }}>{row.amount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ── Invoices table ──────────────────────────────────────── */}
                <div style={{ border: "1px solid #E8EAED", borderRadius: 8, background: "#fff", overflow: "hidden" }}>
                  {/* Section header */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid #E8EAED" }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#EEF0FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#0517F8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1.5" y="1.5" width="9" height="9" rx="1.5" />
                        <path d="M3.5 4h3.5M3.5 6h5M3.5 8h2" />
                        <path d="M8.5 7.5a1 1 0 100 2 1 1 0 000-2z" fill="#0517F8" stroke="none" />
                      </svg>
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>Invoices</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#748094", fontFamily: "var(--font-family-base)" }}>({MOCK_COMPANY_INVOICES.length})</span>
                  </div>
                  {/* Table */}
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                      <colgroup>
                        <col style={{ width: 160 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 130 }} />
                        <col style={{ width: 150 }} />
                        <col style={{ width: 110 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          {[
                            { label: "Invoice Number", icon: <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><rect x="1" y="1" width="10" height="10" rx="1.5" /><path d="M3 4h2M3 6.5h6M3 9h4" /></svg> },
                            { label: "Due Date",       icon: <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><rect x="1" y="2" width="10" height="9" rx="1.5" /><path d="M1 5h10M4 1v2M8 1v2" /></svg> },
                            { label: "Amount",         icon: <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><path d="M6 1v10M8.5 3.5a2.5 1.5 0 10-5 0 2.5 1.5 0 005 0zM8.5 8.5a2.5 1.5 0 10-5 0 2.5 1.5 0 005 0z" /></svg> },
                            { label: "Quote #",        icon: <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M10 7.5l-4-4-4 4" /><path d="M6 3.5v7" /></svg> },
                            { label: "Status",         icon: <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><circle cx="6" cy="6" r="5" /><path d="M4 6l1.5 1.5L8 4" /></svg> },
                          ].map(({ label, icon }) => (
                            <th key={label} style={{ background: "var(--color-bg-accent)", borderBottom: "1px solid var(--color-border-primary)", padding: "0 12px", height: 34, whiteSpace: "nowrap", textAlign: "left", fontWeight: "normal" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", userSelect: "none" }}>
                                {icon}{label}
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {MOCK_COMPANY_INVOICES.map((row) => (
                          <tr
                            key={row.id}
                            onMouseEnter={() => setHoveredInvoiceId(row.id)}
                            onMouseLeave={() => setHoveredInvoiceId(null)}
                            style={{ background: hoveredInvoiceId === row.id ? "var(--color-bg-accent)" : "#fff", cursor: "pointer", transition: "background 80ms ease" }}
                          >
                            <td style={{ padding: "0 12px", height: 48, fontSize: 12, fontWeight: 700, color: "#0517F8", fontFamily: "var(--font-family-base)", borderBottom: "1px solid var(--color-border-primary)", verticalAlign: "middle", whiteSpace: "nowrap" }}>{row.number}</td>
                            <td style={{ padding: "0 12px", height: 48, fontSize: 12, fontWeight: 500, color: "#748094",  fontFamily: "var(--font-family-base)", borderBottom: "1px solid var(--color-border-primary)", verticalAlign: "middle", whiteSpace: "nowrap" }}>{row.dueDate}</td>
                            <td style={{ padding: "0 12px", height: 48, fontSize: 12, fontWeight: 600, color: "#172B4D",  fontFamily: "var(--font-family-base)", borderBottom: "1px solid var(--color-border-primary)", verticalAlign: "middle", whiteSpace: "nowrap" }}>{row.amount}</td>
                            <td style={{ padding: "0 12px", height: 48, fontSize: 12, fontWeight: 500, color: "#748094",  fontFamily: "var(--font-family-base)", borderBottom: "1px solid var(--color-border-primary)", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                              {row.quoteNumber
                                ? <span style={{ color: "#0517F8", fontWeight: 700 }}>{row.quoteNumber}</span>
                                : <span style={{ color: "#A2AAB8" }}>—</span>
                              }
                            </td>
                            <td style={{ padding: "0 12px", height: 48, borderBottom: "1px solid var(--color-border-primary)", verticalAlign: "middle" }}><CompanyInvoiceStatusBadge status={row.status} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            )}

            {activeTab === "SLA Compliance" && (
              <div style={{ paddingTop: 16, paddingBottom: 24 }}>
                <SLAComplianceTab companyId={companyId} />
              </div>
            )}

            {activeTab === "Sub Companies" && (
              <SubCompaniesTab companyId={companyId} onOpenCompany={onOpenCompany} />
            )}

            {activeTab !== "Overview" && activeTab !== "SLA Compliance" && activeTab !== "Sub Companies" && (
              <div style={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                justifyContent: "center", paddingTop: 80, gap: 8,
              }}>
                <span style={{ fontSize: 32 }}>🚧</span>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)" }}>
                  {activeTab}
                </p>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
                  This tab is coming soon.
                </p>
              </div>
            )}

          </div>
        </div>

        {/* ── Right sidebar ─────────────────────────────────────────────────── */}
        <div style={{ width: 340, flexShrink: 0, borderLeft: "1px solid #E8EAED", overflowY: "auto", background: "#fff" }}>

          {/* Details block */}
          <div style={{ borderBottom: "1px solid #E8EAED" }}>
            <div
              onClick={() => setDetailsCollapsed((v) => !v)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", cursor: "pointer" }}
            >
              <span style={{ fontSize: 14, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
                Details
              </span>
              {detailsCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
            </div>
            {!detailsCollapsed && (
              <div style={{ padding: "0 16px 8px" }}>
                <SidebarDetailRow icon={<CalendarIcon />} label="Created at" value="12/06/2026" />
                <SidebarDetailRow
                  icon={<UserIcon />}
                  label="Created by"
                  value={
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Avatar initials="JM" color="#2563EB" />
                      <span style={{ fontSize: 12, color: "#172B4D", fontFamily: "var(--font-family-base)", fontWeight: 500 }}>James Mcdonald</span>
                    </div>
                  }
                />
                <SidebarDetailRow icon={<IdIcon />} label="Company ID" value="00001" />
                <SidebarDetailRow icon={<TypeIcon />} label="Company Type" value="Customer" />
                <SidebarDetailRow icon={<WebIcon />} label="Website" value={
                  <a href="#" style={{ color: "#0517F8", fontSize: 12, fontWeight: 500, textDecoration: "none", fontFamily: "var(--font-family-base)" }}>
                    www.acme.com
                  </a>
                } />
                <SidebarDetailRow icon={<LocationIcon />} label="Address" value="40 Lime St, London, EC3M 7AW" />
                <SidebarDetailRow icon={<UserIcon />} label="Team" value={
                  <div style={{ display: "flex", gap: 4 }}>
                    <span style={{ background: "#F3F4F6", borderRadius: 4, padding: "2px 6px", fontSize: 11, fontWeight: 700, color: "#748094", fontFamily: "var(--font-family-base)" }}>SALES</span>
                    <span style={{ background: "#F3F4F6", borderRadius: 4, padding: "2px 6px", fontSize: 11, fontWeight: 700, color: "#748094", fontFamily: "var(--font-family-base)" }}>+1</span>
                  </div>
                } />
              </div>
            )}
          </div>

          {/* Portal block */}
          <div style={{ borderBottom: "1px solid #E8EAED" }}>
            <div
              onClick={() => setPortalCollapsed((v) => !v)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", cursor: "pointer" }}
            >
              <span style={{ fontSize: 14, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
                Portal
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  onClick={(e) => e.stopPropagation()}
                  style={{ border: "none", background: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#D1D5DB", fontFamily: "var(--font-family-base)", padding: 0 }}
                >
                  Create
                </button>
                {portalCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
              </div>
            </div>
            {!portalCollapsed && (
              <div style={{ padding: "0 16px 14px" }}>
                <div style={{
                  border: "1px solid #E8EAED", borderRadius: 8, padding: "10px 12px",
                  display: "flex", alignItems: "center", gap: 10,
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, background: "#FFF7ED",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <PortalIcon />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#172B4D", fontFamily: "var(--font-family-base)", lineHeight: "18px" }}>
                      Acme Portal
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: "16px", display: "flex", gap: 4 }}>
                      <span>20-04-2025</span>
                      <span>·</span>
                      <span>0 Users</span>
                    </div>
                  </div>
                  <button style={{ border: "none", background: "none", cursor: "pointer", color: "#9CA3AF", display: "flex", alignItems: "center", padding: 2 }}>
                    <MoreIcon />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Contracts block */}
          <div style={{ borderBottom: "1px solid #E8EAED" }}>
            <div
              onClick={() => setContractsCollapsed((v) => !v)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", cursor: "pointer" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
                  Contracts
                </span>
                {companyContracts.length > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 700, background: "#EFF6FF", color: "#0517F8", borderRadius: 4, padding: "1px 6px", fontFamily: "var(--font-family-base)" }}>
                    {companyContracts.length}
                  </span>
                )}
              </div>
              {contractsCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
            </div>
            {!contractsCollapsed && (
              <div style={{ padding: "0 16px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
                {companyContracts.length > 0 ? (
                  companyContracts.map((contract) => (
                    <ContractCard
                      key={contract.id}
                      contract={contract}
                      companyName={companyName}
                      onClick={() => setSelectedContract(contract)}
                    />
                  ))
                ) : (
                  <div style={{
                    border: "1.5px dashed #E8EAED", borderRadius: 8, padding: "20px 16px",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8, background: "#F3F4F6",
                      display: "flex", alignItems: "center", justifyContent: "center", color: "#9CA3AF",
                    }}>
                      <ContractDocIcon />
                    </div>
                    <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: "#9CA3AF", fontFamily: "var(--font-family-base)", textAlign: "center" }}>
                      No contracts for this company.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Price Books block */}
          <div style={{ borderBottom: "1px solid #E8EAED" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px" }}>
              <div
                onClick={() => setPriceBookCollapsed((v) => !v)}
                style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flex: 1 }}
              >
                <span style={{ fontSize: 14, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
                  Price Books
                </span>
                {assignedPriceBooks.length > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 700, background: "#EFF6FF", color: "#0517F8", borderRadius: 4, padding: "1px 6px", fontFamily: "var(--font-family-base)" }}>
                    {assignedPriceBooks.length}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button
                  onClick={() => {
                    setAssignedPriceBooks(MOCK_PRICE_BOOKS);
                    setPriceBookCollapsed(false);
                  }}
                  disabled={assignedPriceBooks.length === MOCK_PRICE_BOOKS.length}
                  style={{
                    border: "none", background: "none", padding: "2px 0",
                    fontSize: 12, fontWeight: 600, cursor: assignedPriceBooks.length === MOCK_PRICE_BOOKS.length ? "default" : "pointer",
                    color: assignedPriceBooks.length === MOCK_PRICE_BOOKS.length ? "#9CA3AF" : "#0517F8",
                    fontFamily: "var(--font-family-base)", transition: "color 80ms",
                  }}
                >
                  Assign
                </button>
                <div onClick={() => setPriceBookCollapsed((v) => !v)} style={{ cursor: "pointer" }}>
                  {priceBookCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
                </div>
              </div>
            </div>
            {!priceBookCollapsed && (
              <div style={{ padding: "0 16px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
                {assignedPriceBooks.length > 0 ? (
                  assignedPriceBooks.map((book) => (
                    <PriceBookCard
                      key={book.id}
                      book={book}
                      onRemove={() => setAssignedPriceBooks((prev) => prev.filter((b) => b.id !== book.id))}
                    />
                  ))
                ) : (
                  <div style={{
                    border: "1.5px dashed #E8EAED", borderRadius: 8, padding: "20px 16px",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8, background: "#F3F4F6",
                      display: "flex", alignItems: "center", justifyContent: "center", color: "#9CA3AF",
                    }}>
                      <PriceBookIcon />
                    </div>
                    <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: "#9CA3AF", fontFamily: "var(--font-family-base)", textAlign: "center" }}>
                      No price books assigned.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Custom Fields block */}
          <div>
            <div
              onClick={() => setCustomFieldsCollapsed((v) => !v)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", cursor: "pointer" }}
            >
              <span style={{ fontSize: 14, fontWeight: 700, color: "#172B4D", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
                Custom Fields
              </span>
              {customFieldsCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
            </div>
            {!customFieldsCollapsed && (
              <div style={{ padding: "0 16px 8px" }}>
                {[
                  { label: "Multi Select", value: <span style={{ background: "#F6F7FF", borderRadius: 4, padding: "2px 6px", fontSize: 11, fontWeight: 700, color: "#6974FB", fontFamily: "var(--font-family-base)" }}>CUSTOM</span> },
                  { label: "Single Select", value: <span style={{ background: "#F6F7FF", borderRadius: 4, padding: "2px 6px", fontSize: 11, fontWeight: 700, color: "#6974FB", fontFamily: "var(--font-family-base)" }}>CUSTOM</span> },
                  { label: "Text", value: <span style={{ fontSize: 12, color: "#172B4D", fontFamily: "var(--font-family-base)", fontWeight: 500 }}>Something here...</span> },
                  { label: "Number", value: <span style={{ fontSize: 12, color: "#172B4D", fontFamily: "var(--font-family-base)", fontWeight: 500 }}>—</span> },
                  { label: "Date", value: <span style={{ fontSize: 12, color: "#172B4D", fontFamily: "var(--font-family-base)", fontWeight: 500 }}>—</span> },
                ].map(({ label, value }) => (
                  <div key={label} style={{ display: "grid", gridTemplateColumns: "140px 1fr", alignItems: "center", minHeight: 32, paddingTop: 4, paddingBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>{label}</span>
                    <div>{value}</div>
                  </div>
                ))}
                <button style={{ border: "none", background: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#0517F8", fontFamily: "var(--font-family-base)", padding: "8px 0 4px", display: "block" }}>
                  Show Empty Fields
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Contract Detail Panel */}
      {selectedContract && (
        <ContractDetailPanel
          contract={selectedContract}
          onClose={() => setSelectedContract(null)}
        />
      )}

    </div>
  );
}
